import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, deleteField, where } from "firebase/firestore";
import { Search, Plus, Edit2, FileText, Trash2, Filter, ChevronRight, Clock, CheckCircle, ArrowDownLeft } from 'lucide-react';
import Spinner from './Spinner.js';

const MaterialInward = ({ db }) => {
  const navigate = useNavigate();
  const [mics, setMics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('All');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    // Show all MIC-related statuses (mic_draft onwards)
    const q = query(
      collection(db, "inwards"),
      orderBy("updatedAt", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const micList = snapshot.docs
        .filter(doc => {
          const status = doc.data().status;
          // Show all MIC workflow statuses, including records handed off to QC.
          return status === 'mic_draft' || status === 'mic_ready' || status === 'mic_completed' || status === 'qc_pending';
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      setMics(micList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching MICs:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredMics = useMemo(() => {
    return mics.filter(mic => {
      const matchesSearch = 
        mic.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (mic.supplierName || '')?.toLowerCase().includes(searchTerm.toLowerCase()) || // supplierName might not exist on PC-originated MICs
        mic.micNo?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesWarehouse = filterWarehouse === 'All' || mic.warehouse === filterWarehouse;

      return matchesSearch && matchesWarehouse;
    });
  }, [mics, searchTerm, filterWarehouse]);

  const handleDiscardMIC = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Discard this MIC progress? This will reset the status back to 'Pickup Ready' and wipe the inward checklist data. The original Pickup Challan will remain safe.")) return;
    
      try {
      const docRef = doc(db, "inwards", id);
      await updateDoc(docRef, {
        status: 'pc_ready', // Revert to PC Ready status
        micStatus: deleteField(),
        micComponents: deleteField(),
        micNo: deleteField(),
        micDate: deleteField(),
        typeOfMIC: deleteField(),
        shippingAddress: deleteField(),
        warehouse: deleteField(),
        preparedBy: deleteField(),
        quantityVerifiedBy: deleteField(),
        vehicleNumber: deleteField(),
        vehicleInTime: deleteField(),
        vehicleOutTime: deleteField(),
        vehicleDateTime: deleteField(),
        verifiedByFinance: deleteField(),
        declarationDate: deleteField(),
        updatedAt: serverTimestamp()
      });
      alert("MIC Discarded. Document reverted to Pickup stage.");
    } catch (e) {
      alert("Error discarding MIC: " + e.message);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
        case 'mic_draft':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#f0fdf4', color: '#166534' }}>
                <Clock size={12}/> MIC DRAFT
            </span>;
        case 'mic_ready':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#fdf2f2', color: '#dc2626' }}>
                <Clock size={12}/> MIC READY
            </span>;
        case 'mic_completed':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#dcfce7', color: '#15803d' }}>
                <CheckCircle size={12}/> MIC COMPLETED
            </span>;
        case 'qc_pending':
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#eef2ff', color: '#4338ca' }}>
                <Clock size={12}/> QC PENDING
            </span>;
        default:
            return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', backgroundColor: '#f3f4f6', color: '#64748b' }}>
                <Clock size={12}/> {status?.toUpperCase().replace(/_/g, ' ') || 'DRAFT'}
            </span>;
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ArrowDownLeft size={24} color="#2563eb" /> Material In Checklists
          </h2>
          <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '14px' }}>Manage inbound material checklists and inspections.</p>
        </div>
        <button 
          onClick={() => navigate('/ops/mic/new')} 
          style={createBtnStyle}
        >
          <Plus size={18} /> {isMobile ? 'New' : 'Create MIC'}
        </button>
      </div>

      {/* FILTERS */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={searchIconStyle} size={18} />
          <input 
            type="text" 
            placeholder="Search by Supplier/Customer or MIC No..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Filter size={18} color="#64748b" />
          <select 
            value={filterWarehouse} 
            onChange={(e) => setFilterWarehouse(e.target.value)}
            style={selectInputStyle}
          >
            <option value="All">All Warehouses</option>
            <option value="Jaipur">Jaipur</option>
            <option value="Ahmedabad">Ahmedabad</option>
            <option value="Bhiwandi">Bhiwandi</option>
            <option value="New Delhi">New Delhi</option>
            <option value="Hyderabad">Hyderabad</option>
          </select>
        </div>
      </div>

      {/* TABLE / LIST */}
      <div style={containerStyle}>
        {filteredMics.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <FileText size={48} style={{ marginBottom: '10px', opacity: 0.5 }} />
            <p>No Material In Checklists found.</p>
          </div>
        ) : (
          <table style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>MIC No.</th>
                <th style={thStyle}>Supplier / Customer</th>
                <th style={thStyle}>Document Type</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMics.map((mic) => (
                <tr 
                  key={mic.id} 
                  style={trStyle} 
                  onClick={() => navigate(`/ops/mic/preview/${mic.id}`)}
                >
                  <td style={tdStyle}>{mic.createdAt?.toDate ? mic.createdAt.toDate().toLocaleDateString('en-IN') : mic.micDate}</td>
                  <td style={{ ...tdStyle, fontWeight: 'bold', color: '#2563eb' }}>{mic.inwardNo || mic.micNo}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: '600' }}>{mic.customerName || mic.supplierName || 'N/A'}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{mic.referenceNo}</div>
                  </td>
                  <td style={tdStyle}>{mic.type || 'Pickup'}</td>
                  <td style={tdStyle}>{mic.warehouse || '-'}</td>
                  <td style={tdStyle}>{getStatusBadge(mic.status)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                      <button 
                        onClick={(e) => { e.stopPropagation(); navigate(`/ops/mic/edit/${mic.id}`); }} 
                        style={actionBtnStyle} 
                        title="Edit"
                      >
                        <Edit2 size={16}/>
                      </button>
                      <button 
                        onClick={(e) => handleDiscardMIC(e, mic.id)} 
                        style={{ ...actionBtnStyle, color: '#ef4444' }} 
                        title="Delete"
                      >
                        <Trash2 size={16}/>
                      </button>
                      <ChevronRight size={18} color="#cbd5e1" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// --- STYLES ---
const containerStyle = {
  backgroundColor: 'white',
  borderRadius: '10px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden'
};

const filterBarStyle = {
  display: 'flex',
  gap: '15px',
  marginBottom: '20px',
  flexWrap: 'wrap'
};

const searchInputStyle = {
  width: '100%',
  padding: '10px 10px 10px 40px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  outline: 'none'
};

const searchIconStyle = {
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#94a3b8'
};

const selectInputStyle = {
  padding: '10px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  backgroundColor: 'white',
  outline: 'none',
  minWidth: '160px'
};

const createBtnStyle = {
  backgroundColor: '#1e293b',
  color: 'white',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '8px',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const theadStyle = {
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0'
};

const thStyle = {
  padding: '12px 15px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: 'bold',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const trStyle = {
  borderBottom: '1px solid #f1f5f9',
  cursor: 'pointer',
  transition: 'background-color 0.2s'
};

const tdStyle = {
  padding: '15px',
  fontSize: '14px',
  color: '#1e293b'
};

const actionBtnStyle = {
  background: 'none',
  border: 'none',
  padding: '5px',
  color: '#64748b',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center'
};

export default MaterialInward;
